import type { Vehicle } from "@/app/services/generated/vehicleService";
import { VehicleService } from "@/app/services/generated/vehicleService";
import type Guid from "@/app/types/common/guid";
import { GuidMap } from "@/app/types/common/guidcollections";
import type { Instant } from "@js-joda/core";

export default class VehicleServiceExtended extends VehicleService {
  private routeColours: string[] = [
    "#00CEC9",
    "#3584DC",
    "#FF4500",
    "#DC2A2A",
    "#6861DF",
    "#D85291",
    "#4FB596",
    "#C7403A",
    "#81B9F9",
    "#D35400",
    "#F1B4A4",
    "#A09EF7",
    "#EE81A7",
    "#80EBC6",
    "#F07E7A",
  ];

  /**
   * Get a vehicle's display colour. If no colour is set, returns a default
   * colour.
   */
  getVehicleDisplayColour(
    vehicleId: Guid,
    vehicles?: readonly Readonly<Vehicle>[]
  ) {
    const displayColour = vehicles?.find((vehicle) =>
      vehicle.id.equals(vehicleId)
    )?.displayColour;

    if (displayColour) {
      return displayColour;
    }

    const sortedVehiclesWithNoDisplayColour = this.getSortedVehiclesByDate(
      vehicles
    ).filter((vehicle) => !vehicle.displayColour);

    let vehiclePosition = sortedVehiclesWithNoDisplayColour.findIndex(
      (vehicle) => vehicle.id.equals(vehicleId)
    );

    if (vehiclePosition === -1) {
      vehiclePosition = 0;
    }

    return this.routeColours[vehiclePosition % this.routeColours.length];
  }

  /**
   * Returns a sorted array of vehicles. Does not mutate the original array.
   */
  private getSortedVehiclesByDate(vehicles: readonly Readonly<Vehicle>[] = []) {
    return [...vehicles].sort((a, b) =>
      (a.readOnly?.createdAt as Instant).compareTo(
        b.readOnly?.createdAt as Instant
      )
    );
  }

  /**
   * Returns a map of vehicles to their display colour. If a vehicle has no
   * display colour, a default value will be used.
   */
  getVehicleDisplayColourMap(vehicles: readonly Readonly<Vehicle>[]) {
    const vehicleColourMap = new GuidMap<string>();

    vehicles.forEach((vehicle, index) => {
      if (vehicle.displayColour) {
        vehicleColourMap.set(vehicle.id, vehicle.displayColour);
      }
    });

    const sortedVehiclesWithNoDisplayColour = this.getSortedVehiclesByDate(
      vehicles
    ).filter((vehicle) => !vehicle.displayColour);

    sortedVehiclesWithNoDisplayColour.forEach((vehicle, index) => {
      const colour = this.routeColours[index % this.routeColours.length];
      vehicleColourMap.set(vehicle.id, colour);
    });

    return vehicleColourMap;
  }
}

export const vehicleService = new VehicleServiceExtended();
